import styled from "styled-components"
import { TextDiv, TextSpan } from "../common/Text"
import { useRecoilValue } from "recoil"
import useUpdateQuizResponse from "../../hooks/QuizPage/useUpdateQuizResponse"
import { useEffect, useRef } from "react"
import { QuizItemType } from "../../types"
import {
  focusedItemIdxState,
  quizResponseListState,
} from "../../recoil/QuizPage/atoms"
import quizSections from "./quizSections"

interface Props {
  item: QuizItemType
  itemIdx: number
  sectionIdx: number
  setRef: (ref: React.RefObject<HTMLDivElement>) => void
}

const QuizItem = ({ item, itemIdx, sectionIdx, setRef }: Props) => {
  const quizResponseList = useRecoilValue(quizResponseListState)
  const focusedItemIdx = useRecoilValue(focusedItemIdxState)
  const { updateQuizResponse } = useUpdateQuizResponse()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setRef(ref)
  }, [setRef])

  useEffect(() => {
    if (focusedItemIdx === itemIdx) {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [focusedItemIdx, itemIdx])

  return (
    <QuizItemDiv
      ref={ref}
      key={`item-${itemIdx}`}
      className={itemIdx === focusedItemIdx ? "focus" : ""}
    >
      <QuizDescriptionDiv>
        <TextSpan fontSize={16} fontWeight="bold">
          {`[${item.item}] ${
            item.description
              ? item.description
              : "(이 문항은 별도의 설명이 없습니다)"
          }`}
        </TextSpan>
      </QuizDescriptionDiv>
      <ButtonContainer id={`response-${item.item_id}`}>
        {quizSections[sectionIdx].responseLabels.map((option, index) => {
          return (
            <ButtonDiv>
              <RadioButton
                id={`response-${item.item_id}-${index}`}
                key={`response-${item.item_id}-${index}`}
                value={index}
                $colorIdx={index}
                checked={quizResponseList[itemIdx]?.likert === index + 1}
                onClick={() => {
                  updateQuizResponse({
                    itemId: item.item_id,
                    likert: index + 1,
                    itemIdx: itemIdx,
                  })
                }}
              />
            </ButtonDiv>
          )
        })}
      </ButtonContainer>
      <LabelContainer>
        {quizSections[sectionIdx].responseLabels.map((option) => {
          return <TextDiv fontSize={12}>{option}</TextDiv>
        })}
      </LabelContainer>
    </QuizItemDiv>
  )
}

export default QuizItem

const QuizDescriptionDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ButtonContainer = styled.div`
  margin-top: 15px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  & > * {
    width: 52px;
  }

  @media ${({ theme }) => theme.device.pc} {
    width: 60%;
    margin: 15px auto 0px;
  }
`

const LabelContainer = styled.div`
  margin-top: 5px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  & > * {
    width: 52px;
  }

  @media ${({ theme }) => theme.device.pc} {
    width: 60%;
    margin: 15px auto 0px;
  }
`

const ButtonDiv = styled.div`
  display: flex;
  justify-content: center;
`

const QuizItemDiv = styled.div`
  width: 100%;
  border-radius: 20px;
  box-sizing: border-box;
  background-color: var(--custom-white-smoke);
  padding: 20px 10px;
  transition: all 0.2s ease;
  opacity: 0.5;

  &.focus {
    opacity: 1;
    transform: scale(1.05);
  }
`

const RadioButton = styled.input.attrs(() => ({
  type: "radio",
  readOnly: true,
}))<{ $colorIdx: number }>`
  margin: 0;
  appearance: none;
  border: 0px solid var(--custom-platinum);
  outline: 2px solid var(--custom-dark-brown);
  border-radius: 50%;
  width: ${(props) =>
    props.$colorIdx === 0
      ? "30px"
      : props.$colorIdx === 1
      ? "25px"
      : props.$colorIdx === 2
      ? "22px"
      : props.$colorIdx === 3
      ? "25px"
      : "30px"};
  height: ${(props) =>
    props.$colorIdx === 0
      ? "30px"
      : props.$colorIdx === 1
      ? "25px"
      : props.$colorIdx === 2
      ? "22px"
      : props.$colorIdx === 3
      ? "25px"
      : "30px"};
  transition: border 0.1s ease;

  &:checked {
    border-width: 6px;
    background-color: ${(props) =>
      props.$colorIdx === 0
        ? "var(--custom-light-red)"
        : props.$colorIdx === 1
        ? "var(--custom-salmon-pink)"
        : props.$colorIdx === 2
        ? "var(--custom-gray)"
        : props.$colorIdx === 3
        ? "var(--custom-light-blue)"
        : "var(--custom-blue)"};
  }

  &:hover {
    border-width: 6px;
    background-color: ${(props) =>
      props.$colorIdx === 0
        ? "var(--custom-light-red)"
        : props.$colorIdx === 1
        ? "var(--custom-salmon-pink)"
        : props.$colorIdx === 2
        ? "var(--custom-gray)"
        : props.$colorIdx === 3
        ? "var(--custom-light-blue)"
        : "var(--custom-blue)"};
  }
`
